/**
 * Admin-SPA error reporting.
 *
 * Same pipeline as customer storefronts — `POST /public/telemetry/errors` with
 * `surface: 'admin'` — so a white screen in the admin lands in the same Slack
 * channel and the same Errors list as a customer-site failure.
 *
 * WHY THIS IS A MODULE AND NOT AN INLINE TAG (as it is for storefronts):
 * storefront HTML is assembled by the platform at dispatch time, so we can splice
 * a handler into <head> ahead of the site's own bundle. Here the bundle IS ours,
 * and `main.tsx` imports this first — early enough for everything except a
 * failure of the entry chunk itself. That residual gap is covered differently:
 * a chunk error triggers a one-shot reload, which is the recovery that matters.
 *
 * Reports go to the `__platform__` bucket (super-admin only in the UI), because
 * an admin-app bug isn't any single customer's problem.
 */

const ENDPOINT = `${import.meta.env.VITE_API_URL || '/api/v1'}/public/telemetry/errors`;
const RELOAD_GUARD = '__tyTelReload';

let installed = false;
let sent = 0;
const MAX_REPORTS_PER_SESSION = 8;
const seen = new Set<string>();

function sessionId(): string {
  try {
    const k = '__tyTelSid';
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return 'nostore';
  }
}

function isChunkError(message: string): boolean {
  return /Loading chunk|ChunkLoadError|Importing a module script failed|Failed to fetch dynamically imported module|Unexpected token '<'/i.test(
    message
  );
}

function send(kind: string, message: string, stack?: string): void {
  if (sent >= MAX_REPORTS_PER_SESSION) return;
  const key = `${kind}|${message.slice(0, 120)}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent++;

  const body = JSON.stringify({
    projectId: '__platform__',
    surface: 'admin',
    sessionId: sessionId(),
    events: [{ kind, message, stack, url: location.href, ts: Date.now() }],
  });

  try {
    // text/plain keeps this a CORS simple request (no preflight) and survives
    // unload — which matters because the next line may reload the page.
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
    if (navigator.sendBeacon?.(ENDPOINT, blob)) return;
  } catch {
    /* fall through to fetch */
  }
  void fetch(ENDPOINT, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    keepalive: true,
  }).catch(() => {
    /* reporting must never surface an error of its own */
  });
}

/**
 * A stale index.html after a deploy references chunk hashes that no longer
 * exist; the fetch returns an HTML 404 where JS was expected and the app dies
 * with a blank screen. One hard reload picks up current HTML and recovers.
 * Guarded so a genuinely broken build cannot reload-loop.
 */
function recoverFromChunkError(): void {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD)) return;
    sessionStorage.setItem(RELOAD_GUARD, '1');
    setTimeout(() => location.reload(), 150);
  } catch {
    /* private mode — skip recovery rather than risk a loop */
  }
}

export function installTelemetry(): void {
  if (installed) return;
  installed = true;

  window.addEventListener(
    'error',
    (e) => {
      try {
        const target = e.target as (HTMLElement & { src?: string; href?: string }) | null;
        if (target && target !== (window as unknown) && (target.src || target.href)) {
          const src = target.src || target.href || '';
          if (!/^https?:/i.test(src)) return;
          send('resource', `Failed to load ${target.tagName}: ${src}`);
          if (/\/assets\/|\.m?js(\?|$)/.test(src)) recoverFromChunkError();
          return;
        }
        const message = e.message || 'Unknown error';
        send(isChunkError(message) ? 'chunk' : 'error', message, e.error?.stack);
        if (isChunkError(message)) recoverFromChunkError();
      } catch {
        /* never throw from the error handler */
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason = e.reason;
      const message = String(reason?.message ?? reason);
      send(isChunkError(message) ? 'chunk' : 'unhandledrejection', message, reason?.stack);
      if (isChunkError(message)) recoverFromChunkError();
    } catch {
      /* never throw from the rejection handler */
    }
  });

  // Clear the one-shot guard once the app has run cleanly, so a future stale
  // deploy is allowed its own single recovery attempt.
  setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD);
    } catch {
      /* ignore */
    }
  }, 8_000);
}
